/**
 * shared/kiemTraDeck.js: kiểm tra số liệu pitch deck bằng code.
 *
 * Luồng (functions/index.js, chayNhanXetAI):
 *   1. AI đọc PDF và chỉ TRÍCH XUẤT số liệu theo KHUON_TRICH_XUAT (không nhận xét).
 *   2. kiemTraSoLieu() chạy các quy tắc dưới đây: toán vòng gọi vốn, burn và runway,
 *      pha loãng, lợi nhuận và ROI lúc exit, IRR, biểu đồ so với bảng, tổng so với
 *      thành phần, độ dốc dự phóng, thị phần ngầm định, lãi so với nhu cầu vốn,
 *      tăng trưởng không có số gốc, phần còn thiếu, chỉ số SaaS so với ngưỡng
 *      tham chiếu (rời bỏ, giữ chân doanh thu ròng, hoàn vốn CAC, tập trung khách
 *      hàng), cờ đỏ về cách ghi nhận doanh thu, slide exit không tính pha loãng.
 *   3. Kết quả được đưa cho AI nhận xét như sự thật đã kiểm chứng, và hiện trên
 *      trang quản trị để người review chọn đưa vào nhận xét gửi founder.
 *   4. gioiHanTruc(): trục có lỗi nghiêm trọng không được chấm cao hơn mức trần.
 *
 * Hàm thuần, không đụng tới Firebase. Build chép sang functions/chung/.
 */

/** Mức độ của một phát hiện, xếp từ nặng tới nhẹ. */
export const MUC_PHAT_HIEN = {
  'co-do': 'Cờ đỏ',
  'can-sua': 'Cần sửa',
  'nen-sua': 'Nên cải thiện',
};
const THU_TU = { 'co-do': 0, 'can-sua': 1, 'nen-sua': 2 };

/** Phát hiện ảnh hưởng tới trục chấm điểm nào (khớp TRUC trong nhanXetDeck.js). */
export const TRUC_CUA_NHOM = {
  'goi-von': 'tai-chinh',
  'exit': 'tai-chinh',
  'tai-chinh': 'tai-chinh',
  'bang-chung': 'bang-chung',
  'thi-truong': 'thi-truong',
  'mo-hinh': 'mo-hinh',
  'doi-ngu': 'doi-ngu',
  'trinh-bay': 'trinh-bay',
};

// ---------------------------------------------------------------------------
// Khuôn trích xuất
// ---------------------------------------------------------------------------
export const KHUON_TRICH_XUAT = `{
  "congTy": "tên công ty",
  "tienTe": "USD | VND | khác",
  "namLap": "năm lập deck nếu suy ra được (năm có số thực tế gần nhất), không rõ thì null",
  "chuoiSo": [ {
      "chiSo": "tên chuẩn hoá: revenue | nmv | gmv | ebitda | net_income | users | customers | burn | headcount | tên khác viết thường không dấu",
      "nhan": "nhãn gốc trên slide",
      "donVi": "USD | VND | % | count | null nếu slide không ghi",
      "slide": 1,
      "nguon": "chart | table | text",
      "diem": [ { "ky": "2021 hoặc 2021f hoặc Q1/2024", "giaTri": 1800000, "duPhong": false } ]
  } ],
  "quanHeTong": [ { "tong": "chiSo của chuỗi tổng", "thanhPhan": ["chiSo các chuỗi thành phần"], "slide": 1 } ],
  "vongGoi": {
    "soTien": null, "preMoney": null, "postMoney": null, "hinhThuc": "equity | safe | convertible | null",
    "runwayThang": null, "burnThang": null, "nhanSuHienTai": null, "nhanSuDuKien": null,
    "phanBo": [ { "hangMuc": "tên", "phanTram": null, "soTien": null } ],
    "cotMoc": ["cột mốc sẽ đạt bằng số tiền gọi, nếu deck có ghi"],
    "slide": null
  },
  "exit": {
    "dinhGia": null, "nam": null, "coSoDinhGia": "revenue | nmv | gmv | ebitda | users | khac | null", "heSo": null,
    "phanTramNhaDauTu": null, "loiNhuan": null, "roi": null, "soNamToiThieu": null, "soNamToiDa": null,
    "thuongVu": [ { "ten": "", "nam": null, "quocGia": null, "giaTri": null, "heSo": null } ],
    "nguoiMua": [], "slide": null
  },
  "thiTruong": { "tam": null, "sam": null, "som": null, "slide": null },
  "kinhTeDonVi": { "cac": null, "ltv": null, "takeRate": null, "bienGop": null, "paybackThang": null,
    "churnThang": null, "nrr": null, "khachLonNhatPhanTram": null, "slide": null },
  "canhBaoDoanhThu": [ { "loai": "gmv_la_doanh_thu | tcv_la_doanh_thu | cam_ket_la_doanh_thu | phi_mot_lan_trong_dinh_ky | dang_ky_la_nguoi_dung",
      "cau": "nguyên văn câu hoặc nhãn trên slide", "slide": 1 } ],
  "tangTruong": [ { "cau": "nguyên văn, vd Traffic +900%", "slide": 1, "coSoGoc": false, "coThoiGian": false } ],
  "thieu": ["mã các phần deck KHÔNG có: cap_table | esop | unit_economics | bien_gop_theo_dong | doi_thu | doi_ngu | tiep_can_thi_truong | duong_den_loi_nhuan | phan_bo_von"]
}`;

export const QUY_TAC_TRICH_XUAT = `Bạn là trợ lý trích xuất số liệu từ pitch deck. Nhiệm vụ DUY NHẤT: đọc deck và ghi số liệu vào khuôn JSON.

Quy tắc bắt buộc:
1. Không nhận xét, không đánh giá, không tự tính thêm con số nào không có trên slide.
2. Quy đổi K, M, B, nghìn, triệu, tỷ ra số đầy đủ (205K thành 205000; 1,8 tỷ thành 1800000000).
3. Con số xuất hiện ở cả biểu đồ và bảng: ghi thành HAI chuỗi riêng với nguon "chart" và "table". Không gộp, không sửa cho khớp.
4. Kỳ có hậu tố f, F, E, (dự phóng), forecast, hoặc ở tương lai so với namLap: duPhong = true.
5. Slide không ghi đơn vị: donVi = null. Không đoán đơn vị.
6. % lợi nhuận gộp, take rate, % phân bổ: ghi số từ 0 đến 100.
7. Thiếu thông tin thì null hoặc mảng rỗng. Tuyệt đối không bịa.
8. churnThang: tỷ lệ khách rời bỏ MỖI THÁNG (%); nếu slide chỉ ghi theo năm thì để null. nrr: giữ chân doanh thu ròng (%). khachLonNhatPhanTram: % doanh thu từ khách lớn nhất, chỉ khi slide ghi rõ.
9. canhBaoDoanhThu: chỉ ghi khi chữ trên slide cho thấy RÕ một trong các trường hợp: GMV hoặc NMV được gọi là doanh thu (gmv_la_doanh_thu); tổng giá trị hợp đồng nhiều năm được gọi là doanh thu hoặc ARR (tcv_la_doanh_thu); thư cam kết, MOU, pilot miễn phí được đếm là doanh thu hoặc khách trả tiền (cam_ket_la_doanh_thu); phí triển khai, phí một lần được cộng vào MRR hoặc ARR (phi_mot_lan_trong_dinh_ky); số người đăng ký được gọi là người dùng hoặc khách hàng mà không nói số người dùng hoạt động (dang_ky_la_nguoi_dung). Không chắc thì không ghi.
10. Chỉ trả về đúng một khối JSON, không viết gì khác.`;

// ---------------------------------------------------------------------------
// Định dạng số kiểu Việt Nam
// ---------------------------------------------------------------------------
const laSo = (x) => typeof x === 'number' && Number.isFinite(x);
const gon = (x, le = 1) => Number(x.toFixed(le)).toString().replace('.', ',');
export const pt = (x, le = 1) => `${gon(x, le)}%`;

export function tien(n, tienTe = 'USD') {
  if (!laSo(n)) return 'không rõ';
  const dau = n < 0 ? '-' : '';
  const a = Math.abs(n);
  const donVi = tienTe === 'VND' ? ' đồng' : ` ${tienTe || 'USD'}`;
  if (tienTe === 'VND') {
    if (a >= 1e9) return `${dau}${gon(a / 1e9)} tỷ`;
    if (a >= 1e6) return `${dau}${gon(a / 1e6)} triệu`;
    return `${dau}${a.toLocaleString('vi-VN')}${donVi}`;
  }
  if (a >= 1e9) return `${dau}${gon(a / 1e9)} tỷ${donVi}`;
  if (a >= 1e6) return `${dau}${gon(a / 1e6)} triệu${donVi}`;
  if (a >= 1e3) return `${dau}${gon(a / 1e3)} nghìn${donVi}`;
  return `${dau}${gon(a)}${donVi}`;
}

const lech = (a, b) => Math.abs(a - b) / Math.max(Math.abs(a), Math.abs(b), 1e-9);
const namCua = (ky) => { const m = String(ky).match(/(19|20)\d{2}/); return m ? Number(m[0]) : null; };
const chuanKy = (ky) => String(ky).toLowerCase().replace(/[^0-9a-z]/g, '').replace(/(f|e|forecast)$/, '');
const duyNhat = (a) => [...new Set(a.filter((x) => x != null && x !== ''))];
const tenNguon = (s) => ({ chart: 'biểu đồ', table: 'bảng', text: 'phần chữ' }[s] || s);
const irr = (boi, nam) => (Math.pow(boi, 1 / nam) - 1) * 100;

// Mốc so sánh. Quy đổi VND sang USD xấp xỉ để dùng chung một ngưỡng.
const TY_GIA = 25000;
const raUSD = (n, tienTe) => (tienTe === 'VND' ? n / TY_GIA : n);

function ph(p) {
  return { slides: [], bangChung: [], goiY: '', ...p };
}

// ---------------------------------------------------------------------------
// 1. Vòng gọi vốn
// ---------------------------------------------------------------------------
function kiemVongGoi(d, ctx) {
  const ra = [];
  const v = d.vongGoi || {};
  const tt = d.tienTe;
  const sl = duyNhat([v.slide]);

  if (laSo(v.soTien) && laSo(v.preMoney)) {
    const post = v.preMoney + v.soTien;
    const tyLe = (v.soTien / post) * 100;
    ctx.soLieu.postMoney = post;
    ctx.soLieu.tyLeVongNay = tyLe;
    if (laSo(v.postMoney) && lech(v.postMoney, post) > 0.01) {
      ra.push(ph({ ma: 'vong.post-money', muc: 'co-do', nhom: 'goi-von', slides: sl,
        tieuDe: 'Post-money không bằng pre-money cộng số tiền gọi',
        chiTiet: `Pre-money ${tien(v.preMoney, tt)} cộng ${tien(v.soTien, tt)} phải ra ${tien(post, tt)}, nhưng deck ghi ${tien(v.postMoney, tt)}.`,
        goiY: 'Thống nhất một con số định giá và ghi rõ là pre-money hay post-money.' }));
    }
    if (tyLe > 30) {
      ra.push(ph({ ma: 'vong.pha-loang-cao', muc: 'can-sua', nhom: 'goi-von', slides: sl,
        tieuDe: `Vòng này pha loãng ${pt(tyLe)} cổ phần`,
        chiTiet: `Nhà đầu tư vòng này sở hữu ${pt(tyLe)} (post-money ${tien(post, tt)}). Mức thường gặp cho một vòng là 15 đến 25%; cao hơn khiến các vòng sau khó gọi.`,
        goiY: 'Giảm số tiền gọi, hoặc giải thích vì sao cần mức này, kèm cap table sau vòng.' }));
    }
  } else if (laSo(v.soTien) && !laSo(v.preMoney) && !laSo(v.postMoney) && v.hinhThuc !== 'safe' && v.hinhThuc !== 'convertible') {
    ra.push(ph({ ma: 'vong.chua-dinh-gia', muc: 'nen-sua', nhom: 'goi-von', slides: sl,
      tieuDe: 'Có số tiền gọi nhưng chưa có định giá',
      chiTiet: 'Không có pre-money hoặc post-money nên nhà đầu tư chưa biết mình nhận bao nhiêu % cổ phần.',
      goiY: 'Ghi mức định giá dự kiến, hoặc ghi rõ gọi bằng SAFE hay khoản vay chuyển đổi kèm mức trần định giá.' }));
  }

  if (laSo(v.soTien) && laSo(v.runwayThang) && v.runwayThang > 0) {
    const burn = v.soTien / v.runwayThang;
    ctx.soLieu.burnNgamDinh = burn;
    if (laSo(v.burnThang) && lech(burn, v.burnThang) > 0.15) {
      ra.push(ph({ ma: 'vong.burn-lech', muc: 'can-sua', nhom: 'goi-von', slides: sl,
        tieuDe: 'Runway không khớp với mức đốt tiền đã nêu',
        chiTiet: `${tien(v.soTien, tt)} chia ${v.runwayThang} tháng là khoảng ${tien(burn, tt)} mỗi tháng, nhưng deck ghi burn ${tien(v.burnThang, tt)} mỗi tháng.`,
        goiY: 'Tính lại runway từ burn thực tế, có tính doanh thu dự kiến trong giai đoạn đó.' }));
    }
    const ns = v.nhanSuDuKien ?? v.nhanSuHienTai;
    if (laSo(ns) && ns > 0) {
      const moiNguoi = burn / ns;
      ctx.soLieu.burnMoiNguoi = moiNguoi;
      const usd = raUSD(moiNguoi, tt);
      if (usd > 12000 || usd < 1500) {
        ra.push(ph({ ma: 'vong.burn-moi-nguoi', muc: 'nen-sua', nhom: 'goi-von', slides: sl,
          tieuDe: `Chi phí khoảng ${tien(moiNguoi, tt)} mỗi người mỗi tháng`,
          chiTiet: `Burn ${tien(burn, tt)} mỗi tháng cho ${ns} người. Với startup tại Việt Nam, mức này ${usd > 12000 ? 'cao' : 'thấp'} bất thường; nhà đầu tư sẽ hỏi tiền đi đâu.`,
          goiY: 'Thêm bảng chi phí theo nhóm (nhân sự, marketing, hạ tầng) theo quý.' }));
      }
    }
  }

  const pb = v.phanBo || [];
  const cacPhanTram = pb.map((x) => x.phanTram).filter(laSo);
  if (cacPhanTram.length && cacPhanTram.length === pb.length) {
    const tong = cacPhanTram.reduce((a, b) => a + b, 0);
    if (Math.abs(tong - 100) > 0.5) {
      ra.push(ph({ ma: 'vong.phan-bo-tong', muc: 'can-sua', nhom: 'goi-von', slides: sl,
        tieuDe: `Phân bổ vốn cộng lại ${pt(tong)}, không phải 100%`,
        chiTiet: pb.map((x) => `${x.hangMuc} ${pt(x.phanTram, 0)}`).join(', ') + '.',
        goiY: 'Rà lại tỷ lệ phân bổ cho tổng đúng 100%.' }));
    }
    const chung = pb.find((x) => /^(operation|operations|vận hành|van hanh|other|khác|general|chi phí chung)/i.test(String(x.hangMuc || '').trim()) && x.phanTram >= 40);
    if (chung) {
      ra.push(ph({ ma: 'vong.phan-bo-chung-chung', muc: 'nen-sua', nhom: 'goi-von', slides: sl,
        tieuDe: `Hạng mục "${chung.hangMuc}" chiếm ${pt(chung.phanTram, 0)} nhưng quá chung chung`,
        chiTiet: 'Nhà đầu tư muốn biết tiền biến thành kết quả gì. Một hạng mục chung chung chiếm gần nửa số vốn sẽ bị hỏi kỹ.',
        goiY: 'Tách thành khoản cụ thể (tuyển bao nhiêu người, vị trí gì) và gắn mỗi khoản với một cột mốc.' }));
    }
  }
  if (laSo(v.soTien) && !(v.cotMoc || []).length) {
    ra.push(ph({ ma: 'vong.khong-cot-moc', muc: 'can-sua', nhom: 'goi-von', slides: sl,
      tieuDe: 'Chưa nói số tiền này sẽ đưa công ty tới đâu',
      chiTiet: 'Deck có số tiền gọi nhưng không có cột mốc cần đạt trước vòng sau (doanh thu, số khách, thị trường mới).',
      goiY: 'Thêm 3 đến 4 cột mốc đo được, ví dụ "đạt X doanh thu mỗi tháng và Y khách trả phí sau 18 tháng".' }));
  }
  return ra;
}

// ---------------------------------------------------------------------------
// 2. Exit
// ---------------------------------------------------------------------------
function kiemExit(d, ctx) {
  const ra = [];
  const e = d.exit || {};
  const v = d.vongGoi || {};
  const tt = d.tienTe;
  const slE = duyNhat([e.slide]);
  const slCa = duyNhat([v.slide, e.slide]);

  if (laSo(e.phanTramNhaDauTu) && laSo(e.dinhGia)) {
    const ln = (e.phanTramNhaDauTu / 100) * e.dinhGia;
    ctx.soLieu.loiNhuanNgamDinh = ln;
    if (laSo(e.loiNhuan) && lech(ln, e.loiNhuan) > 0.05) {
      ra.push(ph({ ma: 'exit.loi-nhuan-lech', muc: 'co-do', nhom: 'exit', slides: slE,
        tieuDe: 'Lợi nhuận nhà đầu tư không khớp với % sở hữu nhân định giá exit',
        chiTiet: `${pt(e.phanTramNhaDauTu)} của ${tien(e.dinhGia, tt)} là ${tien(ln, tt)}, nhưng deck ghi ${tien(e.loiNhuan, tt)}.`,
        goiY: 'Tính lại bảng lợi nhuận bằng công thức để nhà đầu tư tự kiểm được.' }));
    }
  }

  const loiNhuan = e.loiNhuan ?? ctx.soLieu.loiNhuanNgamDinh;
  if (laSo(loiNhuan) && laSo(v.soTien) && laSo(e.roi)) {
    const roi = loiNhuan / v.soTien;
    if (lech(roi, e.roi) > 0.05) {
      ra.push(ph({ ma: 'exit.roi-lech', muc: 'co-do', nhom: 'exit', slides: slCa,
        tieuDe: `ROI ghi ${gon(e.roi)}x nhưng tính ra ${gon(roi)}x`,
        chiTiet: `Lợi nhuận ${tien(loiNhuan, tt)} chia số tiền vòng này ${tien(v.soTien, tt)} là ${gon(roi)}x.`,
        goiY: 'Sửa ROI theo đúng công thức, hoặc ghi rõ ROI tính trên số tiền nào.' }));
    }
  }

  if (laSo(ctx.soLieu.tyLeVongNay) && laSo(e.phanTramNhaDauTu)) {
    const dau = ctx.soLieu.tyLeVongNay;
    const cuoi = e.phanTramNhaDauTu;
    if (Math.abs(dau - cuoi) > 1) {
      const giam = (1 - cuoi / dau) * 100;
      ra.push(ph({ ma: 'exit.ty-le-lech-vong', muc: cuoi < dau ? 'can-sua' : 'co-do', nhom: 'exit', slides: slCa,
        tieuDe: `Vòng này nhận ${pt(dau)} nhưng slide exit tính ${pt(cuoi)}`,
        chiTiet: cuoi < dau
          ? `Ngầm định nhà đầu tư bị pha loãng khoảng ${pt(giam, 0)} qua các vòng sau, nhưng deck không giải thích.${laSo(e.dinhGia) ? ` Nếu không pha loãng, phần của họ là ${tien((dau / 100) * e.dinhGia, tt)}.` : ''}`
          : 'Tỷ lệ lúc exit lại cao hơn lúc đầu tư, điều không thể xảy ra nếu nhà đầu tư không mua thêm.',
        goiY: 'Thêm bảng pha loãng dự kiến qua các vòng sau để giải thích con số lúc exit.' }));
    }
  }

  const roiTinh = e.roi ?? (laSo(loiNhuan) && laSo(v.soTien) ? loiNhuan / v.soTien : null);
  if (laSo(roiTinh) && roiTinh > 0 && (laSo(e.soNamToiThieu) || laSo(e.soNamToiDa))) {
    const it = e.soNamToiThieu ?? e.soNamToiDa;
    const nhieu = e.soNamToiDa ?? e.soNamToiThieu;
    const thap = irr(roiTinh, nhieu);
    const cao = irr(roiTinh, it);
    ctx.soLieu.irr = [thap, cao];
    ra.push(ph({ ma: 'exit.irr', muc: 'nen-sua', nhom: 'exit', slides: slE,
      tieuDe: `IRR ngầm định khoảng ${pt(thap, 0)} đến ${pt(cao, 0)} mỗi năm`,
      chiTiet: `${gon(roiTinh)}x trong ${it === nhieu ? it : `${it} đến ${nhieu}`} năm. ROI này dựa trên dự phóng chưa kiểm chứng nên nhà đầu tư sẽ chiết khấu mạnh.`,
      goiY: 'Thêm kịch bản thận trọng (định giá exit thấp hơn một nửa) để cho thấy sự tỉnh táo.' }));
  }

  const coSo = e.coSoDinhGia;
  if ((coSo === 'nmv' || coSo === 'gmv') && !laSo(d.kinhTeDonVi?.takeRate)) {
    ra.push(ph({ ma: 'exit.dinh-gia-tren-gmv', muc: 'co-do', nhom: 'exit', slides: slE,
      tieuDe: `Định giá dựa trên ${coSo.toUpperCase()} thay vì doanh thu`,
      chiTiet: `${coSo.toUpperCase()} là tổng giá trị giao dịch qua nền tảng, không phải doanh thu của công ty. Thiếu take rate thì nhà đầu tư không quy ra được hệ số trên doanh thu để so với các thương vụ tương đương.`,
      goiY: `Thêm take rate và doanh thu thuần (${coSo.toUpperCase()} nhân take rate), rồi tính hệ số định giá trên doanh thu.` }));
  }

  const tv = e.thuongVu || [];
  if (tv.length) {
    const nams = tv.map((x) => x.nam).filter(laSo);
    const moc = d.namLap || ctx.namHienTai;
    const cu = nams.filter((n) => moc - n > 5).length;
    const van = [];
    if (tv.every((x) => !laSo(x.heSo))) van.push('không thương vụ nào có hệ số định giá (giá trị chia doanh thu), nên chỉ thấy quy mô chứ không so sánh được');
    if (cu) van.push(`${cu}/${tv.length} thương vụ cách thời điểm lập deck hơn 5 năm`);
    if (nams.length > 1 && Math.max(...nams) - Math.min(...nams) > 3) van.push(`các năm trải từ ${Math.min(...nams)} đến ${Math.max(...nams)}, điều kiện thị trường khác nhau`);
    if (van.length) {
      ra.push(ph({ ma: 'exit.thuong-vu-yeu', muc: 'can-sua', nhom: 'exit', slides: slE,
        tieuDe: 'Bảng thương vụ tương đương chưa dùng để định giá được',
        chiTiet: van.join('; ') + '.',
        bangChung: tv.map((x) => `${x.ten}${x.nam ? ` (${x.nam})` : ''}: ${tien(x.giaTri, tt)}`),
        goiY: 'Chọn 4 đến 6 thương vụ trong 3 đến 5 năm gần nhất, ưu tiên Đông Nam Á, ghi hệ số định giá trên doanh thu của từng thương vụ.' }));
    }
  }

  // Phân kỳ định giá (Villalobos, Kauffman): slide exit giữ nguyên tỷ lệ sở hữu
  // của vòng này tới lúc bán công ty, tức ngầm định không có vòng gọi vốn nào nữa.
  const soNam = e.soNamToiThieu ?? e.soNamToiDa ?? (laSo(e.nam) && laSo(d.namLap) ? e.nam - d.namLap : null);
  if (laSo(ctx.soLieu.tyLeVongNay) && laSo(e.phanTramNhaDauTu) && laSo(e.dinhGia)
    && Math.abs(ctx.soLieu.tyLeVongNay - e.phanTramNhaDauTu) <= 1 && (!laSo(soNam) || soNam >= 3)) {
    ra.push(ph({ ma: 'exit.khong-pha-loang', muc: 'can-sua', nhom: 'exit', slides: slCa,
      tieuDe: 'Slide exit giữ nguyên tỷ lệ sở hữu của vòng này tới lúc bán công ty',
      chiTiet: `Deck tính nhà đầu tư vẫn nắm ${pt(e.phanTramNhaDauTu)} khi exit, tức ngầm định công ty không gọi thêm vòng nào. Ở các thương vụ thành công, giá trị cổ phần của nhà đầu tư sớm thường tăng chậm hơn giá trị công ty 3 đến 5 lần vì pha loãng (phân kỳ định giá). Nhà đầu tư có kinh nghiệm sẽ thấy ngay.`,
      goiY: 'Dựng kịch bản có các vòng sau (số vòng, tỷ lệ bán ra mỗi vòng, mở rộng ESOP) và tính lợi nhuận trên tỷ lệ còn lại lúc exit. Công cụ mô phỏng vòng gọi vốn tính sẵn phần này.' }));
  }
  return ra;
}

// ---------------------------------------------------------------------------
// Chỉ số SaaS so với ngưỡng tham chiếu (Umbrex, How to Analyze a SaaS Company,
// 2024). Ngưỡng kiểu Mỹ: dùng để đặt câu hỏi, không phải để kết luận.
// ---------------------------------------------------------------------------
function kiemChiSoSaaS(d) {
  const ra = [];
  const ue = d.kinhTeDonVi || {};
  const sl = duyNhat([ue.slide]);
  if (laSo(ue.churnThang) && ue.churnThang > 3) {
    const cao = ue.churnThang > 7;
    ra.push(ph({ ma: 'saas.roi-bo', muc: cao ? 'can-sua' : 'nen-sua', nhom: 'mo-hinh', slides: sl,
      tieuDe: `Tỷ lệ rời bỏ ${pt(ue.churnThang)} mỗi tháng`,
      chiTiet: `Với ${pt(ue.churnThang)} mỗi tháng, khách ở lại trung bình khoảng ${gon(100 / ue.churnThang, 0)} tháng. Mức tham chiếu cho phần mềm thuê bao: dưới 2 đến 3% mỗi tháng là tốt, trên 5 đến 7% là cao.`,
      goiY: 'Trình bày tỷ lệ rời bỏ theo nhóm khách bắt đầu cùng tháng (cohort) và kế hoạch giữ chân cụ thể.' }));
  }
  if (laSo(ue.nrr) && ue.nrr < 100) {
    ra.push(ph({ ma: 'saas.nrr-duoi-100', muc: 'nen-sua', nhom: 'mo-hinh', slides: sl,
      tieuDe: `Giữ chân doanh thu ròng ${pt(ue.nrr, 0)}, dưới 100%`,
      chiTiet: 'Dưới 100% nghĩa là doanh thu từ khách hiện có đang co lại; công ty phải liên tục tìm khách mới chỉ để đứng yên.',
      goiY: 'Cho thấy lộ trình nâng gói, bán thêm hoặc giảm rời bỏ để đưa con số này lên trên 100%.' }));
  }
  if (laSo(ue.paybackThang) && ue.paybackThang > 12) {
    ra.push(ph({ ma: 'saas.hoan-von-cac', muc: ue.paybackThang > 24 ? 'can-sua' : 'nen-sua', nhom: 'mo-hinh', slides: sl,
      tieuDe: `Mất ${gon(ue.paybackThang, 0)} tháng để hoàn vốn chi phí có một khách`,
      chiTiet: 'Mức tham chiếu là dưới 12 tháng; công ty có vốn mạo hiểm thường được kỳ vọng 6 đến 9 tháng. Hoàn vốn càng chậm, càng cần nhiều vốn để tăng trưởng.',
      goiY: 'Tính lại theo lãi gộp trên mỗi khách (không theo doanh thu), và nêu cách rút ngắn: kênh rẻ hơn, giá cao hơn, thu trước theo năm.' }));
  }
  if (laSo(ue.khachLonNhatPhanTram) && ue.khachLonNhatPhanTram >= 30) {
    ra.push(ph({ ma: 'saas.tap-trung-khach', muc: 'can-sua', nhom: 'mo-hinh', slides: sl,
      tieuDe: `Một khách hàng chiếm ${pt(ue.khachLonNhatPhanTram, 0)} doanh thu`,
      chiTiet: 'Mất khách này là mất một phần lớn doanh thu. Nhà đầu tư sẽ hỏi hợp đồng còn bao lâu và khả năng gia hạn.',
      goiY: 'Ghi thời hạn hợp đồng, lịch sử gia hạn, và kế hoạch giảm tỷ trọng khách lớn nhất trong 12 tháng.' }));
  }
  return ra;
}

// ---------------------------------------------------------------------------
// Cờ đỏ về cách ghi nhận doanh thu, phỏng theo "Seven Major Shenanigans"
// (Schilit) và bẫy "thước đo do chính ban điều hành chọn" (Chanos).
// ---------------------------------------------------------------------------
const CO_DO_DOANH_THU = {
  gmv_la_doanh_thu: ['co-do', 'GMV hoặc NMV được trình bày như doanh thu',
    'Tổng giá trị giao dịch qua nền tảng không phải doanh thu của công ty. Gọi nhầm làm quy mô trông lớn hơn nhiều lần và mất niềm tin khi thẩm định.',
    'Ghi doanh thu thuần (giá trị giao dịch nhân take rate) bên cạnh GMV, ghi rõ nhãn từng con số.'],
  tcv_la_doanh_thu: ['co-do', 'Tổng giá trị hợp đồng nhiều năm được gọi là doanh thu',
    'Hợp đồng 3 năm không phải doanh thu của năm nay. Doanh thu định kỳ năm (ARR) chỉ tính phần thuộc về một năm.',
    'Tách tổng giá trị hợp đồng (TCV), giá trị hợp đồng mỗi năm (ACV) và doanh thu đã ghi nhận.'],
  cam_ket_la_doanh_thu: ['co-do', 'Thư cam kết hoặc pilot miễn phí được tính như doanh thu',
    'Thư cam kết và triển khai thử là bằng chứng nhu cầu, chưa phải doanh thu. Gộp vào doanh thu là ghi nhận trước khi thực sự kiếm được.',
    'Trình bày riêng: khách đã trả tiền, pilot có thu phí, thư cam kết; mỗi nhóm một con số.'],
  phi_mot_lan_trong_dinh_ky: ['can-sua', 'Phí một lần được cộng vào doanh thu định kỳ',
    'Phí triển khai, phí cài đặt không lặp lại. Cộng vào MRR hoặc ARR làm tăng trưởng định kỳ trông tốt hơn thực tế.',
    'Tách doanh thu định kỳ và doanh thu một lần thành hai dòng.'],
  dang_ky_la_nguoi_dung: ['can-sua', 'Số đăng ký được gọi là người dùng',
    'Nhà đầu tư quan tâm người dùng hoạt động, không phải số tài khoản đã tạo.',
    'Ghi người dùng hoạt động hằng tháng (MAU) và tỷ lệ hoạt động trên số đăng ký.'],
};

function kiemCoDoDoanhThu(d) {
  const ra = [];
  const theoLoai = {};
  for (const x of d.canhBaoDoanhThu || []) {
    if (!CO_DO_DOANH_THU[x?.loai]) continue;
    (theoLoai[x.loai] = theoLoai[x.loai] || []).push(x);
  }
  for (const [loai, ds] of Object.entries(theoLoai)) {
    const [muc, tieuDe, chiTiet, goiY] = CO_DO_DOANH_THU[loai];
    ra.push(ph({ ma: `doanh-thu.${loai}`, muc, nhom: 'bang-chung', slides: duyNhat(ds.map((x) => x.slide)),
      tieuDe, chiTiet, goiY, bangChung: duyNhat(ds.map((x) => x.cau)) }));
  }
  return ra;
}

// ---------------------------------------------------------------------------
// 3. Nhất quán số liệu
// ---------------------------------------------------------------------------
function kiemNhatQuan(d) {
  const ra = [];
  const tt = d.tienTe;
  const chuoi = d.chuoiSo || [];
  const theoChiSo = {};
  for (const c of chuoi) (theoChiSo[c.chiSo] ||= []).push(c);

  for (const [cs, ds] of Object.entries(theoChiSo)) {
    for (let i = 0; i < ds.length; i++) {
      for (let j = i + 1; j < ds.length; j++) {
        const a = ds[i], b = ds[j];
        const mapA = new Map((a.diem || []).map((p) => [chuanKy(p.ky), p]));
        const mapB = new Map((b.diem || []).map((p) => [chuanKy(p.ky), p]));
        const khac = [];
        for (const p of a.diem || []) {
          const q = mapB.get(chuanKy(p.ky));
          if (q && laSo(p.giaTri) && laSo(q.giaTri) && lech(p.giaTri, q.giaTri) > 0.004)
            khac.push(`${p.ky}: ${tien(p.giaTri, tt)} so với ${tien(q.giaTri, tt)}`);
        }
        if (khac.length) {
          ra.push(ph({ ma: `nhat-quan.${cs}.so`, muc: 'can-sua', nhom: 'tai-chinh', slides: duyNhat([a.slide, b.slide]),
            tieuDe: `"${a.nhan}" ở ${tenNguon(a.nguon)} và ${tenNguon(b.nguon)} ghi khác nhau`,
            chiTiet: `Cùng một chỉ số nhưng hai chỗ ghi khác nhau. ${khac.join('; ')}.`,
            goiY: 'Dùng một nguồn số liệu duy nhất cho cả biểu đồ và bảng.' }));
        }
        const chiA = (a.diem || []).filter((p) => !mapB.has(chuanKy(p.ky))).map((p) => p.ky);
        const chiB = (b.diem || []).filter((p) => !mapA.has(chuanKy(p.ky))).map((p) => p.ky);
        if (chiA.length || chiB.length) {
          const phan = [];
          if (chiA.length) phan.push(`${tenNguon(a.nguon)} có ${chiA.join(', ')} mà ${tenNguon(b.nguon)} không có`);
          if (chiB.length) phan.push(`${tenNguon(b.nguon)} có ${chiB.join(', ')} mà ${tenNguon(a.nguon)} không có`);
          ra.push(ph({ ma: `nhat-quan.${cs}.ky`, muc: 'nen-sua', nhom: 'trinh-bay', slides: duyNhat([a.slide, b.slide]),
            tieuDe: `"${a.nhan}" thiếu kỳ khi so ${tenNguon(a.nguon)} với ${tenNguon(b.nguon)}`,
            chiTiet: `${phan.join('; ')}. Nhà đầu tư sẽ hỏi vì sao bỏ kỳ đó.`,
            goiY: 'Hiện đủ các kỳ ở cả hai chỗ.' }));
        }
      }
    }
  }

  for (const qh of d.quanHeTong || []) {
    const tong = chuoi.find((c) => c.chiSo === qh.tong);
    const tp = (qh.thanhPhan || []).map((m) => chuoi.find((c) => c.chiSo === m)).filter(Boolean);
    if (!tong || !tp.length || tp.length !== (qh.thanhPhan || []).length) continue;
    const sai = [];
    for (const p of tong.diem || []) {
      const gt = tp.map((c) => (c.diem || []).find((q) => chuanKy(q.ky) === chuanKy(p.ky))?.giaTri);
      if (!laSo(p.giaTri) || gt.some((x) => !laSo(x))) continue;
      const cong = gt.reduce((x, y) => x + y, 0);
      if (lech(cong, p.giaTri) > 0.02) sai.push(`${p.ky}: cộng lại ${cong.toLocaleString('vi-VN')}, tổng ghi ${p.giaTri.toLocaleString('vi-VN')}`);
    }
    if (sai.length) {
      const thieuDonVi = tp.some((c) => !c.donVi);
      ra.push(ph({ ma: `nhat-quan.tong.${qh.tong}`, muc: 'can-sua', nhom: 'tai-chinh', slides: duyNhat([qh.slide, tong.slide]),
        tieuDe: `Các dòng thành phần không cộng ra ${tong.nhan}`,
        chiTiet: `${sai.slice(0, 3).join('; ')}.${thieuDonVi ? ' Các dòng thành phần không có đơn vị nên không rõ là số lượng hay giá trị tiền.' : ''}`,
        goiY: 'Ghi đơn vị cho từng dòng. Nếu là số lượng giao dịch, thêm dòng giá trị tiền tương ứng để tổng khớp.' }));
    }
  }

  const khongDonVi = chuoi.filter((c) => !c.donVi);
  if (khongDonVi.length) {
    ra.push(ph({ ma: 'trinh-bay.thieu-don-vi', muc: 'nen-sua', nhom: 'trinh-bay', slides: duyNhat(khongDonVi.map((c) => c.slide)),
      tieuDe: `${khongDonVi.length} chỉ số không ghi đơn vị`,
      chiTiet: khongDonVi.map((c) => `"${c.nhan}" (slide ${c.slide})`).join(', ') + '.',
      goiY: 'Ghi rõ đơn vị (đồng, USD, số lượng, %) ngay trên nhãn cột hoặc dòng.' }));
  }
  return ra;
}

// ---------------------------------------------------------------------------
// 4. Dự phóng
// ---------------------------------------------------------------------------
function chuoiChinh(d) {
  for (const cs of ['revenue', 'nmv', 'gmv']) {
    const ds = (d.chuoiSo || []).filter((c) => c.chiSo === cs);
    if (ds.length) return [...ds].sort((a, b) => (b.diem || []).length - (a.diem || []).length)[0];
  }
  return null;
}

function kiemDuPhong(d, ctx) {
  const ra = [];
  const c = chuoiChinh(d);
  if (!c) return ra;
  const tt = d.tienTe;
  const diem = (c.diem || []).filter((p) => laSo(p.giaTri) && namCua(p.ky)).sort((a, b) => namCua(a.ky) - namCua(b.ky));
  const thuc = diem.filter((p) => !p.duPhong);
  const du = diem.filter((p) => p.duPhong);
  if (!du.length) return ra;

  if (thuc.length) {
    const cuoiThuc = thuc[thuc.length - 1];
    const cuoiDu = du[du.length - 1];
    const nam = namCua(cuoiDu.ky) - namCua(cuoiThuc.ky);
    if (nam > 0 && cuoiThuc.giaTri > 0) {
      const boi = cuoiDu.giaTri / cuoiThuc.giaTri;
      const cagr = irr(boi, nam);
      ctx.soLieu.tangTruongDuPhong = cagr;
      let lichSu = null;
      if (thuc.length >= 2 && thuc[0].giaTri > 0) {
        const n = namCua(cuoiThuc.ky) - namCua(thuc[0].ky);
        if (n > 0) lichSu = irr(cuoiThuc.giaTri / thuc[0].giaTri, n);
      }
      if (cagr > 60) {
        ra.push(ph({ ma: 'du-phong.qua-doc', muc: cagr > 200 ? 'co-do' : cagr > 100 ? 'can-sua' : 'nen-sua', nhom: 'tai-chinh', slides: duyNhat([c.slide]),
          tieuDe: `Phần dự phóng tăng ${gon(boi)} lần trong ${nam} năm (khoảng ${pt(cagr, 0)} mỗi năm)`,
          chiTiet: `Số thực tế cuối cùng là ${tien(cuoiThuc.giaTri, tt)} (${cuoiThuc.ky}), dự phóng ${tien(cuoiDu.giaTri, tt)} (${cuoiDu.ky}).${lichSu != null ? ` Tăng trưởng thực tế đã đạt khoảng ${pt(lichSu, 0)} mỗi năm.` : ''} Khi khoe tăng trưởng, chỉ nên dùng phần thực tế; phần dự phóng cần giả định chứng minh.`,
          bangChung: diem.map((p) => `${p.ky}${p.duPhong ? ' (dự phóng)' : ''}: ${tien(p.giaTri, tt)}`),
          goiY: 'Thêm slide giả định (khách mới mỗi tháng, giá trị trung bình, tỷ lệ giữ chân, kênh tăng trưởng). Tách màu thực tế và dự phóng trên biểu đồ.' }));
      }
    }
  }

  const tm = d.thiTruong || {};
  const goc = tm.sam ?? tm.tam;
  const cuoiDu = du[du.length - 1];
  if (laSo(goc) && goc > 0) {
    const thiPhan = (cuoiDu.giaTri / goc) * 100;
    if (thiPhan > 10) {
      const ten = tm.sam != null ? 'SAM' : 'TAM';
      ra.push(ph({ ma: 'du-phong.thi-phan', muc: thiPhan > 30 ? 'co-do' : 'can-sua', nhom: 'thi-truong', slides: duyNhat([c.slide, tm.slide]),
        tieuDe: `Dự phóng ngầm định chiếm ${pt(thiPhan, 0)} ${ten}`,
        chiTiet: `${c.nhan} ${cuoiDu.ky} là ${tien(cuoiDu.giaTri, tt)} so với ${ten} ${tien(goc, tt)}.`,
        goiY: 'Rà lại quy mô thị trường hoặc dự phóng, và trình bày thị phần theo từng năm.' }));
    }
  }
  return ra;
}

// ---------------------------------------------------------------------------
// 5. Lãi so với nhu cầu vốn
// ---------------------------------------------------------------------------
function kiemLaiVaVon(d, ctx) {
  const ra = [];
  const v = d.vongGoi || {};
  const tt = d.tienTe;
  const eb = (d.chuoiSo || []).find((c) => c.chiSo === 'ebitda' || c.chiSo === 'net_income');
  if (!eb) return ra;
  const diem = (eb.diem || []).filter((p) => laSo(p.giaTri) && namCua(p.ky)).sort((a, b) => namCua(a.ky) - namCua(b.ky));

  const laiDau = diem.find((p) => p.giaTri > 0);
  if (laiDau && laSo(v.soTien) && laSo(v.runwayThang)) {
    const nam = d.namLap || ctx.namHienTai;
    if (namCua(laiDau.ky) <= Math.ceil(nam + v.runwayThang / 12)) {
      ra.push(ph({ ma: 'lai.mau-thuan-runway', muc: 'can-sua', nhom: 'tai-chinh', slides: duyNhat([eb.slide, v.slide]),
        tieuDe: `Dự phóng có lãi từ ${laiDau.ky}, nhưng vẫn cần ${v.runwayThang} tháng runway`,
        chiTiet: `${eb.nhan} ${laiDau.ky} là ${tien(laiDau.giaTri, tt)}, nằm trong thời gian dùng vốn vòng này. Nếu sắp có lãi thì vì sao cần đốt khoảng ${tien(ctx.soLieu.burnNgamDinh, tt)} mỗi tháng? Nhà đầu tư sẽ hỏi ngay câu này.`,
        goiY: 'Thêm bảng dòng tiền theo quý trong giai đoạn runway, cho thấy tiền vòng này bù vào khoảng nào trước khi có lãi.' }));
    }
  }
  for (let i = 1; i < diem.length; i++) {
    const a = diem[i - 1], b = diem[i];
    if (a.giaTri < 0 && b.giaTri > 0 && Math.abs(b.giaTri - a.giaTri) > Math.abs(a.giaTri) * 1.5) {
      ra.push(ph({ ma: 'lai.dao-chieu', muc: 'nen-sua', nhom: 'tai-chinh', slides: duyNhat([eb.slide]),
        tieuDe: `${eb.nhan} đảo từ ${tien(a.giaTri, tt)} (${a.ky}) sang ${tien(b.giaTri, tt)} (${b.ky})`,
        chiTiet: 'Bước chuyển từ lỗ sang lãi lớn cần lý do cụ thể (tăng giá, giảm chi phí, lợi thế quy mô).',
        goiY: 'Thêm slide đường đến lợi nhuận: biên lợi nhuận gộp và chi phí cố định thay đổi thế nào qua từng năm.' }));
      break;
    }
  }
  return ra;
}

// ---------------------------------------------------------------------------
// 6. Tuyên bố tăng trưởng và phần còn thiếu
// ---------------------------------------------------------------------------
const PHAN_THIEU = {
  cap_table: ['Cap table trước và sau vòng', 'can-sua', 'goi-von'],
  esop: ['Quỹ cổ phần cho nhân viên (ESOP)', 'nen-sua', 'goi-von'],
  bien_gop_theo_dong: ['Biên lợi nhuận gộp theo từng dòng sản phẩm', 'can-sua', 'mo-hinh'],
  doi_thu: ['Phân tích đối thủ', 'can-sua', 'mo-hinh'],
  doi_ngu: ['Slide đội ngũ', 'co-do', 'doi-ngu'],
  tiep_can_thi_truong: ['Chiến lược tiếp cận thị trường', 'can-sua', 'thi-truong'],
  duong_den_loi_nhuan: ['Đường đến lợi nhuận', 'nen-sua', 'tai-chinh'],
  phan_bo_von: ['Kế hoạch sử dụng vốn', 'co-do', 'goi-von'],
};

function kiemTuyenBoVaThieu(d) {
  const ra = [];
  const yeu = (d.tangTruong || []).filter((x) => !x.coSoGoc || !x.coThoiGian);
  if (yeu.length) {
    ra.push(ph({ ma: 'bang-chung.khong-so-goc', muc: 'can-sua', nhom: 'bang-chung', slides: duyNhat(yeu.map((x) => x.slide)),
      tieuDe: `${yeu.length} chỉ số tăng trưởng không có số gốc hoặc khoảng thời gian`,
      chiTiet: 'Tăng 900% từ 100 lên 1.000 khác rất xa tăng 900% từ 100.000. Không có số gốc và thời gian, nhà đầu tư sẽ bỏ qua các con số này.',
      bangChung: yeu.map((x) => x.cau),
      goiY: 'Viết dạng "Lượt truy cập tăng từ 20 nghìn lên 200 nghìn mỗi tháng (tháng 1 đến tháng 12/2024)".' }));
  }
  const ue = d.kinhTeDonVi || {};
  const coTaiChinh = (d.chuoiSo || []).length > 0 || laSo(d.vongGoi?.soTien);
  if (coTaiChinh && (!laSo(ue.cac) || !laSo(ue.ltv))) {
    ra.push(ph({ ma: 'mo-hinh.thieu-ltv-cac', muc: 'can-sua', nhom: 'mo-hinh',
      tieuDe: 'Thiếu LTV và CAC',
      chiTiet: 'Giá trị vòng đời khách hàng (LTV) và chi phí có một khách (CAC) là câu hỏi gần như chắc chắn có trong buổi gặp đầu tiên.',
      goiY: 'Thêm LTV, CAC, tỷ lệ LTV:CAC và số tháng hoàn vốn CAC, kể cả khi số còn sớm.' }));
  } else if (laSo(ue.cac) && laSo(ue.ltv) && ue.cac > 0 && ue.ltv / ue.cac < 3) {
    ra.push(ph({ ma: 'mo-hinh.ltv-cac-thap', muc: 'can-sua', nhom: 'mo-hinh',
      tieuDe: `LTV:CAC chỉ ${gon(ue.ltv / ue.cac)}`,
      chiTiet: 'Mức nhà đầu tư thường kỳ vọng là từ 3 trở lên.',
      goiY: 'Giải thích lộ trình cải thiện: giảm CAC qua kênh tự nhiên, tăng giữ chân, tăng giá.' }));
  }
  for (const ma of d.thieu || []) {
    const m = PHAN_THIEU[ma];
    if (!m) continue;
    ra.push(ph({ ma: `thieu.${ma}`, muc: m[1], nhom: m[2],
      tieuDe: `Thiếu: ${m[0]}`,
      chiTiet: 'Nhà đầu tư thường yêu cầu phần này khi thẩm định.' }));
  }
  return ra;
}

// ---------------------------------------------------------------------------
/** Chạy toàn bộ quy tắc trên dữ liệu đã trích xuất. */
export function kiemTraSoLieu(d, { namHienTai = new Date().getFullYear() } = {}) {
  const ctx = { soLieu: {}, namHienTai };
  if (!d || typeof d !== 'object') return { phatHien: [], soLieu: {} };
  const tat = [
    ...kiemVongGoi(d, ctx),
    ...kiemExit(d, ctx),
    ...kiemNhatQuan(d),
    ...kiemDuPhong(d, ctx),
    ...kiemLaiVaVon(d, ctx),
    ...kiemTuyenBoVaThieu(d),
    ...kiemChiSoSaaS(d),
    ...kiemCoDoDoanhThu(d),
  ];
  const daCo = new Set();
  const phatHien = tat
    .filter((x) => (daCo.has(x.ma) ? false : daCo.add(x.ma)))
    .map((x) => ({ ...x, truc: TRUC_CUA_NHOM[x.nhom] || null }))
    .sort((a, b) => THU_TU[a.muc] - THU_TU[b.muc]);
  // Làm tròn để lưu Firestore gọn.
  const soLieu = Object.fromEntries(Object.entries(ctx.soLieu).map(([k, v]) => [k, Array.isArray(v) ? v.map((x) => Math.round(x * 10) / 10) : Math.round(v * 10) / 10]));
  return { phatHien, soLieu };
}

/** Điểm tối đa (thang 5) một trục được nhận khi có các phát hiện này. */
export function tranDiem(maTruc, phatHien = []) {
  const cua = phatHien.filter((x) => x.truc === maTruc);
  if (cua.some((x) => x.muc === 'co-do')) return 2;
  if (cua.filter((x) => x.muc === 'can-sua').length >= 2) return 3;
  if (cua.some((x) => x.muc === 'can-sua')) return 4;
  return 5;
}

/**
 * Áp trần điểm cho các trục của bản nhận xét. Trả về bản mới và danh sách trục
 * bị hạ, để trang quản trị ghi rõ lý do.
 */
export function gioiHanTruc(nx, phatHien = []) {
  const biHa = [];
  const truc = (nx?.truc || []).map((t) => {
    const tran = tranDiem(t.ma, phatHien);
    if (typeof t.diem === 'number' && t.diem > tran) {
      biHa.push({ ma: t.ma, ten: t.ten, tu: t.diem, con: tran });
      return { ...t, diem: tran };
    }
    return t;
  });
  return { nx: { ...nx, truc }, biHa };
}

/** Đoạn văn đưa vào lời nhắc của AI nhận xét. */
export function moTaChoAI(kq, tienTe) {
  if (!kq?.phatHien?.length) return '';
  const dong = kq.phatHien.map((x) => `- [${MUC_PHAT_HIEN[x.muc]}] slide ${x.slides.join(', ') || 'chung'}: ${x.tieuDe}. ${x.chiTiet}`);
  return `<kiem_tra_so_lieu>
Code đã đọc số liệu trong deck và kiểm tra toán học, đối chiếu chéo giữa các slide. Các phát hiện dưới đây ĐÃ ĐƯỢC KIỂM CHỨNG. Không tính lại, không phủ nhận.
Cách dùng: đưa phát hiện "Cờ đỏ" và "Cần sửa" quan trọng nhất vào uuTien hoặc diemDung; ghi vấn đề vào đúng slide trong slides; không coi số dự phóng là điểm mạnh. Trục có cờ đỏ không chấm quá 2/5.
Tiền tệ: ${tienTe || 'không rõ'}. Chỉ số tính sẵn: ${JSON.stringify(kq.soLieu)}
${dong.join('\n')}
</kiem_tra_so_lieu>`;
}
